import type React from 'react';
import { DEFAULT_SPECIALS_CARD_IMG } from 'shared/constants';
import { replaceAWSSourceWithImgix } from 'shared/helpers/imgix';
import {
  formatSpecialsCardDaysLeft,
  getSpecialsCardDaysLeft,
  SPECIALS_CARD_ENDING_SOON_THRESHOLD_DAYS,
} from '@dutchie/common/utils/specials-card';

import type { SpecialsCardBadgeData, SpecialsCardDurationData, SpecialsCardProps } from '../specials-card-types';

export type SpecialMenuCardInput = {
  _id?: string | null;
  name?: string | null;
  menuDisplayName?: string | null;
  menuDisplayDescription?: string | null;
  menuDisplayImage?: string | null;
  specialType?: string | null;
  endStamp?: string | null;
  isRestricted?: boolean | null;
  parentId?: string | null;
};

export type MapToSpecialsCardPropsInput = {
  special: SpecialMenuCardInput;
  href: string;
  specialIndex: number;
  showDuration?: boolean;
  onLinkClick: React.MouseEventHandler<HTMLAnchorElement>;
  isMultiTier?: boolean;
};

const getDurationData = (endStamp: string | null | undefined, showDuration: boolean): SpecialsCardDurationData => {
  if (!showDuration) {
    return {
      label: null,
      isEndingSoon: false,
      endingSoonLabel: null,
    };
  }

  const daysLeft = getSpecialsCardDaysLeft(endStamp);
  if (daysLeft === null) {
    return {
      label: null,
      isEndingSoon: false,
      endingSoonLabel: null,
    };
  }

  const isEndingSoon = daysLeft <= SPECIALS_CARD_ENDING_SOON_THRESHOLD_DAYS;
  return {
    label: formatSpecialsCardDaysLeft(daysLeft),
    isEndingSoon,
    endingSoonLabel: isEndingSoon ? 'Ending soon!' : null,
  };
};

const getBadgeData = (special: SpecialMenuCardInput, isMultiTier: boolean): SpecialsCardBadgeData => {
  const isRestricted = Boolean(special.isRestricted);
  return {
    isRestricted,
    restrictedLabel: isRestricted ? 'Members Only' : null,
    isMultiTier,
    multiTierLabel: isMultiTier ? 'Buy More Save More' : null,
  };
};

/**
 * Pure mapper from internal special card data to SDK SpecialsCardProps (ECOMM-13280).
 * Returns null if the source data is missing required identity (_id or specialType).
 */
export const mapToSpecialsCardProps = ({
  special,
  href,
  specialIndex,
  showDuration = true,
  onLinkClick,
  isMultiTier = false,
}: MapToSpecialsCardPropsInput): SpecialsCardProps | null => {
  if (!special._id || !special.specialType) {
    return null;
  }

  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const displayName = special.menuDisplayName || special.name || 'Special offer';
  const description = special.menuDisplayDescription ?? null;
  const kind: 'offer' | 'sale' = special.specialType === 'sale' ? 'sale' : 'offer';

  const normalizedImage = special.menuDisplayImage ? replaceAWSSourceWithImgix(special.menuDisplayImage) : '';
  const imageUrl = normalizedImage.length > 0 ? normalizedImage : null;

  return {
    special: {
      id: special._id,
      displayName,
      description,
      kind,
      href,
      image: {
        url: imageUrl,
        fallbackUrl: DEFAULT_SPECIALS_CARD_IMG,
        alt: displayName,
      },
      badges: getBadgeData(special, isMultiTier),
      duration: getDurationData(special.endStamp, showDuration),
    },
    specialIndex,
    onLinkClick,
  };
};
